import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { Button } from '../ui/button';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StatusCardAction {
  label: string;
  onClick: () => void;
}

export interface StatusCardProps {
  /** Optional action button at the bottom */
  action?: StatusCardAction;
  /** Description text below the title */
  description: string;
  /** Lucide icon component to display */
  icon: LucideIcon;
  /** Title text */
  title: string;
  /** Visual variant — controls icon background and icon color */
  variant: 'error' | 'success' | 'warning';
}

// ─── Constants ────────────────────────────────────────────────────────────────

const VARIANT_STYLES = {
  error: { bg: 'bg-destructive/10', icon: 'text-destructive' },
  success: { bg: 'bg-success/10', icon: 'text-success' },
  warning: { bg: 'bg-warning/10', icon: 'text-warning' },
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

export function StatusCard({ action, description, icon: Icon, title, variant }: StatusCardProps): React.JSX.Element {
  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm w-full max-w-md text-center">
      <div className="p-6 pt-10 pb-10 space-y-6">
        <div
          className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${VARIANT_STYLES[variant].bg}`}
        >
          <Icon className={`w-8 h-8 ${VARIANT_STYLES[variant].icon}`} />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground">{title}</h2>
          <p className="text-muted-foreground">{description}</p>
        </div>
        {action && (
          <Button className="w-full" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
      </div>
    </div>
  );
}
